import { inject, Injectable } from "@angular/core";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PasswordGenerationService } from "@bitwarden/common/abstractions/passwordGeneration.service";
import { StateService } from "@bitwarden/web-vault/app/core";
import {
  PluginConfigService,
  PluginIdentifier,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { GlossCreditsApi } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits.api";

@Injectable({
  providedIn: "root",
})
export class GlossCreditsUserService {
  readonly pluginName = PluginIdentifier.CREDIT;

  private log: LogService = inject(LogService);
  private passwordGenerationService: PasswordGenerationService = inject(PasswordGenerationService);
  private legacyAppState: StateService = inject(StateService);
  private pluginConfigService: PluginConfigService = inject(PluginConfigService);
  private glossCreditsApi: GlossCreditsApi = inject(GlossCreditsApi);

  async registerUserToGlossBanking(): Promise<{ success: boolean; message: string }> {
    try {
      const username = await this.legacyAppState.getEmail();

      if (!username) {
        return { success: false, message: "Unable to retrieve user email for registration" };
      }

      const dynamoId = await this.legacyAppState.getUserId();
      const password = await this.passwordGenerationService.generatePassword({ length: 14 });
      await this.glossCreditsApi.registerUser(username, password, dynamoId ?? undefined);
      const apiKey = await this.glossCreditsApi.generateApiKey(username, password);

      await this.pluginConfigService.upsertConfig(
        { name: this.pluginName, url: this.glossCreditsApi.baseUrl },
        {
          apiKey,
          username,
          password,
        },
      );

      return { success: true, message: "Registration successful" };
    } catch (error) {
      const errorMsg = `Error during credit registration: ${error?.message || error?.toString() || "Unknown error"}`;
      this.log.error(errorMsg);
      return { success: false, message: errorMsg };
    }
  }
}
